<template>
  <div class="soar-container flex flex-col gap-y-4">
    <ServiceCardSkeleton v-if="isLoading" />
    <template v-else>
      <div class="header">
        <h1 class="text-3xl text-primary rtl:hidden">
          {{ service.shortName.serviceShortNameEnglish }}
        </h1>
        <h1 class="text-3xl text-primary ltr:hidden">
          {{ service.shortName.serviceShortNameArabic }}
        </h1>
      </div>
      <div class="flex flex-col">
        <div
          class="px-4 py-3 w-full flex justify-between bg-brand-gray-300 border border-brand-gray-300"
        >
          <h2 class="text-sm">{{ service.name.text.serviceTransactionName }}</h2>
          <div v-if="service.name.serviceCard.modificationDate" class="flex gap-x-3">
            <span>{{ $t('modificationDate') }}</span>
            <span>
              {{ useDateTime(service.name.serviceCard.modificationDate).format() }}
            </span>
          </div>
        </div>
        <div class="w-full flex flex-col md:flex-row justify-between border border-brand-gray-300">
          <div class="w-full md:w-1/3 px-4 py-3 border border-brand-gray-300 flex flex-col gap-y-2">
            <span>{{ $t('serviceChannels') }}</span>
            <template v-if="serviceChannelIds.value">
              <div
                class="flex gap-x-3"
                :key="key"
                v-for="(sc, key) in serviceChannels.filter((sc) =>
                  serviceChannelIds.value?.includes(sc.id)
                )"
              >
                <i :class="sc.iconClass" class="text-primary-color text-[2rem] text-primary-700" />
              </div>
            </template>
          </div>
          <div class="w-full md:w-2/3 px-4 py-3 border border-brand-gray-300 flex gap-x-4">
            <div class="flex flex-col gap-y-2">
              <span>{{ $t('toShareService') }}</span>
              <div class="flex flex-col md:flex-row gap-3">
                <RouterLink
                  :to="{ name: 'ShareServiceCardView' }"
                  class="flex gap-x-2 text-primary text-start text-xs"
                >
                  <faPaperPlane class="w-3 fill-primary"></faPaperPlane>
                  {{ $t('shareService') }}
                </RouterLink>
                <a
                  :href="qrcode"
                  :download="`Service-${serviceId} QR`"
                  class="flex gap-x-2 text-primary text-start text-xs"
                  @click="onDownloadClick"
                >
                  <faDownload class="w-3 fill-primary"></faDownload>
                  {{ $t('Download QR Code') }}
                </a>
              </div>
            </div>
            <div
              class="w-32 h-32 aspect-square flex justify-center items-center rounded border border-brand-gray-300 shadow-lg"
            >
              <img :src="qrcode" alt="QR Code alt" />
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-y-3 text-primary">
        <h2 class="text-2xl ltr:block rtl:hidden">
          {{ $t('thereAreNSteps', { s: service.shortName.serviceShortNameEnglish }) }}:
        </h2>
        <h2 class="text-2xl ltr:hidden rtl:block">
          {{ $t('thereAreNSteps', { s: service.shortName.serviceShortNameArabic }) }}:
        </h2>
        <div class="w-full py-2 flex justify-between">
          <div
            :key="key"
            v-for="(step, key) in steps"
            class="w-40 flex flex-col gap-y-2 items-center"
          >
            <div class="w-16 md:w-32 relative aspect-square">
              <div
                class="absolute -top-1 -end-1 md:top-0 md:end-1 rounded-full border md:border-2 border-white bg-primary text-white w-7 md:w-8 aspect-square flex justify-center items-center"
              >
                {{ key + 1 }}
              </div>
              <div
                class="w-16 md:w-32 flex justify-center items-center rounded-full aspect-square border-[3px] border-primary"
              >
                <i :class="step.icon" class="text-xl md:text-[3rem]"></i>
              </div>
            </div>
            <span class="text-xs md:text-lg text-center">{{ $t(step.label) }}</span>
          </div>
        </div>
      </div>
      <CollapsibleSection
        v-if="service.content.generalInfoDescription"
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('generalInfoDescription')"
      >
        <div v-html="service.content.generalInfoDescription"></div>
      </CollapsibleSection>
      <CollapsibleSection
        v-if="service.content.serviceRequirements"
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('serviceRequirements')"
      >
        <div v-html="service.content.serviceRequirements"></div>
      </CollapsibleSection>
      <CollapsibleSection
        v-if="service.content.serviceConditionsDescription"
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('serviceConditionsDescription')"
      >
        <div v-html="service.content.serviceConditionsDescription"></div>
      </CollapsibleSection>
      <CollapsibleSection
        v-if="service.content.serviceChannelsDescription"
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('serviceChannelsDescription')"
      >
        <div v-html="service.content.serviceChannelsDescription"></div>
      </CollapsibleSection>

      <CollapsibleSection
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('serviceProcedures')"
      >
        <div v-if="service.content.serviceSteps"></div>
        <div v-else class="flex flex-col gap-y-2.5 divide-y divide-brand-gray-250">
          <!-- based on PBI # 198434 the defualt service procedures should be as the following-->
          <table class="w-full table-auto border-collapse border ltr:font-sans text-xs">
            <thead>
              <tr class="py-2 ps-3 border border-brand-gray-300 text-start">
                <th class="p-1 border border-gray-100 text-primary">
                  {{ i18n.t('ServiceChannel') }}
                </th>
                <th class="p-1 border border-gray-100 text-primary">
                  {{ i18n.t('ServiceCompletionTimeAfterSubmission') }}
                </th>
                <th class="p-1 border border-gray-100 text-primary">
                  {{ i18n.t('ServiceAvailability') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="py-2 ps-3 border border-brand-gray-300 text-start">
                <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs font-bold">
                  {{ i18n.t('ICPSmartServices') }}
                </td>
                <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs">
                  {{ i18n.t('TwoWorkingDays') }}
                </td>
                <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs">
                  {{ i18n.t('Available247') }}
                </td>
              </tr>
              <tr class="py-2 ps-3 border border-brand-gray-300 text-start">
                <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs font-bold">
                  {{ i18n.t('UAEICPMobileApplication') }}
                </td>
                <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs">
                  {{ i18n.t('TwoWorkingDays') }}
                </td>
                <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs">
                  {{ i18n.t('Available247') }}
                </td>
              </tr>
            </tbody>
          </table>

          <div class="flex flex-col gap-y-1 5">
            <h3 class="text-lg text-primary text-center ltr:font-sans">
              {{ i18n.t('ExceptionalCases') }}
            </h3>
            <table class="w-full table-auto border-collapse border ltr:font-sans text-xs">
              <thead>
                <tr class="py-2 ps-3 border border-brand-gray-300 text-start">
                  <th class="p-1 border border-gray-100 text-primary">
                    {{ i18n.t('ServiceChannel') }}
                  </th>
                  <th class="p-1 border border-gray-100 text-primary">
                    {{ i18n.t('ServiceAvailability') }}
                  </th>
                  <th class="p-1 border border-gray-100 text-primary">
                    {{ i18n.t('AverageWaitingTime') }}
                  </th>
                  <th class="p-1 border border-gray-100 text-primary">
                    {{ i18n.t('ServiceCompletionTimeAfterSubmission') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr class="py-2 ps-3 border border-brand-gray-300 text-start">
                  <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs font-bold">
                    {{ i18n.t('CustomerHappinessCenters') }}
                  </td>
                  <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs">
                    {{ i18n.t('Customer_Happiness_Centers_Availability') }}
                  </td>
                  <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs">
                    <div class="w-full h-full flex flex-col gap-y-1.5">
                      <span>
                        {{ i18n.t('WaitingTime') }} =
                        {{ i18n.t('Customer_Happiness_Centers_WaitingTime') }}
                      </span>
                      <span>
                        {{ i18n.t('ServiceDeliveryTime') }} =
                        {{ i18n.t('Customer_Happiness_Centers_ServiceDeliveryTime') }}
                      </span>
                    </div>
                  </td>
                  <td class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs">
                    {{ i18n.t('Customer_Happiness_Centers_CompletionTime') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        v-if="service.content.serviceWorkHoursDescription"
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('serviceWorkHoursDescription')"
      >
        <div v-html="service.content.serviceWorkHoursDescription"></div>
      </CollapsibleSection>
      <CollapsibleSection
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('serviceDocumentsDescription')"
      >
        <div class="flex flex-col gap-y-3 text-brand-black-secondary">
          <div class="flex flex-col md:flex-row md:flex-wrap">
            <div
              :key="key"
              v-for="(att, key) in service.attachments"
              class="w-full md:w-1/3 flex items-start gap-2"
            >
              <faChevronRight class="pt-1.5 w-1.5 fill-brand-black-secondary" />
              <span class="">
                {{ att.localizedName }}
                <span v-if="att.isMandatory" class="text-brand-red">{{ $t('Required') }}</span>
              </span>
            </div>
          </div>
          <p>{{ $t('requiredDocumentsNote') }}</p>
        </div>
      </CollapsibleSection>
      <CollapsibleSection
        v-if="(service.fees?.result?.length || []) > 0"
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('feesInformation')"
      >
        <div class="w-full flex flex-col items-start gap-y-3">
          <table class="w-full md:w-1/2 table-auto border-collapse border ltr:font-sans">
            <tr :key="key" v-for="(row, key) in service.fees.result">
              <td class="px-4 py-2 border border-gray-100 ltr:font-sans text-sm">
                <span class="ltr:hidden rtl:block">{{ row.arabicName }}</span>
                <span class="ltr:block rtl:hidden">{{ row.englishName }}</span>
              </td>
              <td class="px-4 py-2 border border-gray-100 ltr:font-sans text-end">
                {{ intlNumberFormat.format(row.amount) }}
              </td>
            </tr>
          </table>
          <p>{{ $t('serviceFeesNote') }}</p>
        </div>
      </CollapsibleSection>
      <CollapsibleSection
        v-if="service.content.externalLinksDescription"
        :initial-expanded-state="initialCollapsibleState"
        :header-label="$t('externalLinksDescription')"
      >
        <div v-html="service.content.externalLinksDescription"></div>
      </CollapsibleSection>
    </template>
  </div>
</template>

<script setup>
import faChevronRight from '@/assets/icons/faChevronRight.svg?component'
import faDownload from '@/assets/icons/faDownload.svg?component'
import faPaperPlane from '@/assets/icons/faPaperPlane.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import useDateTime from '@/composables/useDateTime'
import useFile from '@/composables/useFile'
import axios from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useGlobalStore } from '@/stores/global'
import { useLookupsStore } from '@/stores/lookups'
import { useQRCode } from '@vueuse/integrations/useQRCode'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

const initialCollapsibleState = false

const intlNumberFormat = Intl.NumberFormat('AE-en', { style: 'currency', currency: 'AED' })
const authStore = useAuthStore()
const globalStore = useGlobalStore()
const lookupStore = useLookupsStore()
const route = useRoute()

const isLoading = ref(true)
const service = ref()
const serviceChannels = ref()

const i18n = useI18n({
  messages: {
    en: {
      ServiceChannel: 'Service Channel',
      ServiceCompletionTimeAfterSubmission: 'Service Completion Time After Submission',
      ServiceAvailability: 'Service Availability',
      TwoWorkingDays: 'Two working days',
      Available247: 'Available 24/7',
      Customer_Happiness_Centers_CompletionTime: 'One working day',
      Customer_Happiness_Centers_Availability: 'According to the announced working hours',
      Customer_Happiness_Centers_WaitingTime: '10 minutes',
      Customer_Happiness_Centers_ServiceDeliveryTime: '7 minutes',
      ICPSmartServices: 'ICP Smart Services',
      UAEICPMobileApplication: 'UAEICP Mobile Application',
      CustomerHappinessCenters: 'Customer Happiness Centers',
      ExceptionalCases: 'Exceptional Cases',
      AverageWaitingTime: 'Average Waiting Time',
      WaitingTime: 'Waiting Time',
      ServiceDeliveryTime: 'Service Delivery Time'
    },
    ar: {
      ServiceChannel: 'قناة الخدمة',
      ServiceCompletionTimeAfterSubmission: 'زمن إنجاز الخدمة بعد اكتمال متطلباتها',
      ServiceAvailability: 'أوقات توفر قناة الخدمة',
      TwoWorkingDays: 'يومين عمل',
      Available247: 'متوفرة 24/7',
      Customer_Happiness_Centers_CompletionTime: 'يوم عمل',
      Customer_Happiness_Centers_Availability: 'وفقًا لساعات العمل المُعلنة',
      Customer_Happiness_Centers_WaitingTime: '10 دقائق',
      Customer_Happiness_Centers_ServiceDeliveryTime: '7 دقائق',
      ICPSmartServices: 'منظومة الخدمات الذكية',
      UAEICPMobileApplication: 'تطبيق الهاتف الذكي UAEICP',
      CustomerHappinessCenters: 'مراكز سعادة المتعاملين',
      ExceptionalCases: 'للحالات الاستثنائية',
      AverageWaitingTime: 'متوسط ​​وقت الانتظار',
      WaitingTime: 'وقت الانتظار',
      ServiceDeliveryTime: 'وقت تقديم الخدمة'
    }
  }
})

const steps = [
  {
    icon: 'ica-custom-fonts-draft o-service-icon-content',
    label: 'fillInformation'
  },
  {
    icon: 'ica-custom-fonts-passport o-service-icon-content',
    label: 'TheAttachments'
  },
  {
    icon: 'ica-custom-fonts-transactions o-service-icon-content',
    label: 'theFees'
  },
  {
    icon: 'ica-custom-fonts-stamp o-service-icon-content',
    label: 'serviceRate'
  },
  {
    icon: 'ica-custom-fonts-stamp-2 o-service-icon-content',
    label: 'EmailResived'
  }
]

const serviceChannelIcons = [
  'fi-mobile-wireframe',
  'fi-generic-icon-desktop-wireframe',
  'fi-service-icon-pin-wireframe',
  'fi-generic-icon-personalize'
]

const serviceChannelIds = computed(() => {
  return service.value.name?.serviceCard?.serviceChannelIDs?.split(',') || []
})

const { host } = window.location
const url = `${host}/services/${route.params.serviceId}`
const qrcode = useQRCode(url)

onMounted(async () => {
  serviceChannels.value = await lookupStore.fetchLookup('/api/lookup/service-channels')
  serviceChannels.value
    .sort((a, b) => a.id - b.id)
    .forEach((sc, index) => {
      serviceChannels.value[index].icon = serviceChannelIcons[index]
    })

  service.value = await globalStore.fetchServiceCard(serviceId.value)
  service.value.shortName = await globalStore.fetchShortName(
    serviceId.value,
    route.query?.applicationId
  )
  // await fetchAttachments()
  isLoading.value = false
})

const serviceId = computed(() => route.params.serviceId)

const onDownloadClick = () => {
  useFile().downloadFile(qrcode, `Service-${serviceId.value} QR`)
}

const fetchAttachments = async () => {
  const path = ['', 'api', 'lookup', 'service', serviceId.value, 'adminRegion', 1]

  if (authStore.isLoggedIn && !authStore.isPublic) {
    path.push(...['applicationId', authStore.user.applicationId])
  }

  path.push('attachmentTypes')

  const attachments = await axios.get(path.join('/'))

  const optionalAttsIds = Object.values(client_lookups.extraOptionalAttachments)
  service.value.attachments = attachments
    .filter((att) => att.attachmentCategoryId != 2)
    .filter((att) => optionalAttsIds.includes(+att.code) === false)
}
</script>

<script>
export default { name: 'ServiceCard' }
</script>

<style lang="scss" scoped></style>
